import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';

const PAGE_SIZE = 20;

const getTargetFlashId = () => {
    if (typeof window === 'undefined') return '';

    const idFromQuery = new URLSearchParams(window.location.search).get('id');

    return idFromQuery ? idFromQuery.trim() : '';
};

const getInitialDisplayCount = (list, targetId) => {
    if (!targetId || !list.length) return PAGE_SIZE;

    const index = list.findIndex(item => `${item.id}` === targetId);

    if (index === -1) return PAGE_SIZE;

    return Math.max(PAGE_SIZE, index + 1);
};

const renderSummary = item => {
    if (!item.summary && !item.summaryHtml) return null;

    if (item.summaryHtml) {
        return <div className={styles.summary} dangerouslySetInnerHTML={{ __html: item.summaryHtml }} />;
    }

    return <p className={styles.summary}>{item.summary}</p>;
};

class FlashList extends PureComponent {
    static propTypes = {
        content: PropTypes.array,
    };

    loadingMore = false;

    loadObserver = null;

    constructor(props) {
        super(props);

        const list = Array.isArray(props.content) ? props.content : [];
        const targetId = getTargetFlashId();

        this.state = {
            displayCount: getInitialDisplayCount(list, targetId),
            targetId,
            hasScrolledToTarget: false,
        };

        this.sentinelRef = React.createRef();
    }

    componentDidMount() {
        this.scrollToTarget();
        this.setupLoadObserver();
    }

    componentDidUpdate(prevProps, prevState) {
        const list = Array.isArray(this.props.content) ? this.props.content : [];
        const prevList = Array.isArray(prevProps.content) ? prevProps.content : [];

        if (list !== prevList && this.state.targetId && !this.state.hasScrolledToTarget) {
            const index = list.findIndex(item => `${item.id}` === this.state.targetId);

            if (index >= this.state.displayCount) {
                this.setState(
                    { displayCount: Math.max(PAGE_SIZE, index + 1) },
                    this.scrollToTarget,
                );

                return;
            }
        }

        if (!this.state.hasScrolledToTarget) {
            this.scrollToTarget();
        }

        if (this.state.displayCount !== prevState.displayCount) {
            if (this.state.displayCount >= list.length) {
                this.teardownLoadObserver();
            } else {
                this.setupLoadObserver();
            }
        }
    }

    componentWillUnmount() {
        this.teardownLoadObserver();
    }

    setupLoadObserver = () => {
        if (typeof window === 'undefined') return;

        this.teardownLoadObserver();

        const list = Array.isArray(this.props.content) ? this.props.content : [];

        if (this.state.displayCount >= list.length || !this.sentinelRef.current) return;

        if (typeof window.IntersectionObserver === 'undefined') {
            window.addEventListener('scroll', this.handleScrollLoad, { passive: true });

            return;
        }

        this.loadObserver = new IntersectionObserver(this.handleSentinelIntersect, {
            root: null,
            rootMargin: '160px 0px',
            threshold: 0,
        });
        this.loadObserver.observe(this.sentinelRef.current);
    };

    teardownLoadObserver = () => {
        if (this.loadObserver) {
            this.loadObserver.disconnect();
            this.loadObserver = null;
        }

        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', this.handleScrollLoad);
        }
    };

    handleSentinelIntersect = entries => {
        if (entries.some(entry => entry.isIntersecting)) {
            this.handleLoadMore();
        }
    };

    handleScrollLoad = () => {
        if (typeof window === 'undefined' || this.loadingMore) return;

        const list = Array.isArray(this.props.content) ? this.props.content : [];

        if (this.state.displayCount >= list.length) return;

        const scrollBottom = window.pageYOffset + window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;

        if (docHeight - scrollBottom <= 160) {
            this.handleLoadMore();
        }
    };

    scrollToTarget = () => {
        if (typeof window === 'undefined' || this.state.hasScrolledToTarget) return;

        const { targetId } = this.state;
        let el = null;

        if (targetId) {
            el = document.getElementById(`flash-${targetId}`);
        }

        if (!el) {
            const list = Array.isArray(this.props.content) ? this.props.content : [];

            if (targetId && list.length) {
                this.setState({ hasScrolledToTarget: true });
            }

            return;
        }

        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.setState({ hasScrolledToTarget: true });
    };

    handleLoadMore = () => {
        const list = Array.isArray(this.props.content) ? this.props.content : [];
        const { displayCount } = this.state;

        if (this.loadingMore || displayCount >= list.length) return;

        this.loadingMore = true;

        this.setState(
            ({ displayCount: currentCount }) => ({
                displayCount: Math.min(currentCount + PAGE_SIZE, list.length),
            }),
            () => {
                this.loadingMore = false;
            },
        );
    };

    renderItem = item => {
        const hasImage = Boolean(item.thumbnail);

        const cardBody = (
            <>
                {hasImage ? (
                    <div className={styles.heroLink}>
                        <img className={styles.heroImg} src={item.thumbnail} alt={item.title || ''} loading="lazy" />
                    </div>
                ) : null}

                {!hasImage && item.title ? <h2 className={styles.cardTitle}>{item.title}</h2> : null}

                {renderSummary(item)}

                {item.tags && item.tags.length ? (
                    <div className={styles.cardFoot}>
                        <div className={styles.tagList}>
                            {item.tags.map(tag => (
                                <span key={tag} className={styles.tagItem}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}
            </>
        );

        return (
            <li key={item.id} id={`flash-${item.id}`} className={styles.card}>
                <div className={styles.cardHead}>
                    {item.important ? (
                        <span className={styles.importantBadge}>重要</span>
                    ) : (
                        <span className={styles.dot} aria-hidden="true" />
                    )}
                    {item.time ? <time className={styles.clock}>{item.time}</time> : null}
                </div>

                {item.url ? (
                    <a href={item.url} className={styles.cardLink} target="_blank" rel="noopener noreferrer">
                        {cardBody}
                    </a>
                ) : (
                    cardBody
                )}
            </li>
        );
    };

    render() {
        const list = Array.isArray(this.props.content) ? this.props.content : [];
        const { displayCount } = this.state;

        if (!list.length) {
            return <div className={styles.emptyTip}>暂无财经快讯，请稍后再试</div>;
        }

        const isEnd = displayCount >= list.length;

        return (
            <div className={styles.listWrap}>
                <ul className={styles.list}>
                    {list.slice(0, displayCount).map(item => this.renderItem(item))}
                </ul>
                {isEnd ? (
                    <div className={styles.endTip}>已显示全部</div>
                ) : (
                    <div ref={this.sentinelRef} className={styles.loadSentinel} aria-hidden="true" />
                )}
            </div>
        );
    }
}

export default errorBoundary(FlashList);
